// oxlint-disable react/only-export-components
import {
  faCheck,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Select as SelectPrimitive } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

/**
 * Figma-Component-Set „Select" bündelt Label-Zeile, Feld und Helfertext in
 * einem Frame — hier bewusst NICHT als Props auf `Select` nachgebaut. Diese
 * App nutzt TanStack Form (`form.AppField`) mit einem generischen
 * `FieldBase`-Wrapper (siehe `packages/ui/src/v2/components/ui/field/field.tsx`,
 * analog zu shadcns `<Field />`), der Label/Description/Required/Error für
 * alle Feldtypen einheitlich rendert (Error kommt dynamisch aus
 * `field.state.meta`, nicht aus einem statischen `invalid`-Prop). Ein
 * eigenes Label/Invalid auf `Select` würde sich damit doppeln oder
 * kollidieren — `Select` bleibt daher ein bare Radix-`Select.Root`, analog
 * zum bestehenden (v1) `Select` in `@e2n/ui`. Für Label/Description/Error
 * `Field`/`FieldContent`/`FieldLabel`/`FieldDescription`/`FieldError`
 * verwenden (siehe `select.stories.tsx`).
 */
function Select({ ...props }: ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectValue({
  ...props
}: ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

type SelectTriggerProps = ComponentProps<typeof SelectPrimitive.Trigger> & {
  leftIcon?: ReactNode
  showLeftIcon?: boolean
  showDot?: boolean
  dotClassName?: string
}

function SelectTrigger({
  className,
  leftIcon,
  showLeftIcon = false,
  showDot = false,
  dotClassName,
  children,
  ...props
}: SelectTriggerProps) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        "group/select-trigger flex h-9 w-full items-center gap-1 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary pr-3 pl-4 text-sm leading-5 whitespace-nowrap text-labels-primary transition-colors outline-none focus-visible:border-brand-blue-100 disabled:cursor-not-allowed disabled:border-neutrals-v2-200 disabled:text-labels-tertiary aria-invalid:border-error-red-200 data-placeholder:text-labels-secondary [&>span]:min-w-0 [&>span]:flex-1 [&>span]:truncate",
        className
      )}
      {...props}
    >
      {showLeftIcon && leftIcon}
      {showDot && (
        <span
          className={cn(
            "size-2.5 shrink-0 rounded-full bg-brand-red-200",
            dotClassName
          )}
        />
      )}
      {children}
      <SelectPrimitive.Icon asChild>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="size-4.5 shrink-0 group-data-[state=open]/select-trigger:hidden"
        />
      </SelectPrimitive.Icon>
      <SelectPrimitive.Icon asChild>
        <FontAwesomeIcon
          icon={faChevronUp}
          className="hidden size-4.5 shrink-0 group-data-[state=open]/select-trigger:block"
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  position = "popper",
  sideOffset = 4,
  ...props
}: ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset}
        className={cn(
          "relative z-50 flex max-h-(--radix-select-content-available-height) min-w-32 flex-col gap-0.5 overflow-hidden rounded-xl border border-neutrals-v2-200 bg-backgrounds-primary py-2 shadow-popover outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "w-full min-w-(--radix-select-trigger-width)",
          className
        )}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            "flex flex-col gap-0.5",
            position === "popper" && "w-full"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
}

type SelectItemProps = ComponentProps<typeof SelectPrimitive.Item> & {
  leftIcon?: ReactNode
  showLeftIcon?: boolean
  showDot?: boolean
  dotClassName?: string
}

/**
 * Figma unterscheidet „hover" (Zeiger) und „focus" (Tastatur) mit
 * unterschiedlicher Optik (Hintergrund vs. Border) — Radix meldet beides
 * einheitlich über `data-highlighted`. Hier bewusst auf den
 * Hintergrund-Stil vereinheitlicht statt eine nicht verfügbare
 * Unterscheidung nachzubauen.
 */
function SelectItem({
  className,
  leftIcon,
  showLeftIcon = false,
  showDot = false,
  dotClassName,
  children,
  ...props
}: SelectItemProps) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-xl px-4 py-2 text-sm leading-5 text-labels-primary outline-none select-none data-disabled:pointer-events-none data-disabled:text-labels-tertiary data-highlighted:bg-neutrals-v2-200",
        className
      )}
      {...props}
    >
      {showLeftIcon && leftIcon}
      {showDot && (
        <span
          className={cn(
            "size-2.5 shrink-0 rounded-full bg-brand-red-200",
            dotClassName
          )}
        />
      )}
      {/* Radix verwirft `className`/`style` auf `ItemText` (der Text wird per Portal in den Trigger gespiegelt) — Layout daher über diesen eigenen Wrapper statt direkt auf `ItemText`. */}
      <span className="min-w-0 flex-1 truncate">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </span>
      <SelectPrimitive.ItemIndicator className="shrink-0">
        <FontAwesomeIcon icon={faCheck} className="size-4.5" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

/**
 * Figma-Varianten „reset button"/„clear button" (beide zeigen „Auswahl
 * entfernen") — kein Radix-`Select.Item`, da Radix keinen leeren
 * Wert-String als Item erlaubt. Der Konsument steuert das Zurücksetzen
 * selbst über `onClick`/`onSelect`.
 */
function SelectClearItem({ className, ...props }: ComponentProps<"button">) {
  return (
    <button
      type="button"
      data-slot="select-clear-item"
      className={cn(
        "flex w-full cursor-pointer items-center rounded-xl border-2 border-transparent px-4 py-2 text-left text-sm leading-5 font-semibold text-labels-primary outline-none hover:bg-neutrals-v2-200 focus-visible:border-brand-blue-100 focus-visible:hover:bg-transparent",
        className
      )}
      {...props}
    />
  )
}

export {
  Select,
  SelectClearItem,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
}
export type { SelectItemProps, SelectTriggerProps }
